import { useContext } from 'react';
import { styled } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import * as Yup from 'yup';
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import { NotificationContext } from '../../../../providers/notification';
import apiCall from '../../../../utils/apiCall';
import { AxiosError } from 'axios';

interface Zone {
    ZoneName: string;
}

interface ComponentProps {
    zoneId: number;
    zoneName: string;
    state: number;
    handleClose: () => void;
}

interface State {
    StateId: number;
    StateName: string;
    CountryId?: number;
    Country: Country;
}

interface Country {
  CountryId: number;
  CountryName: string;
  Id: number;
  CreatedBy?: any;
  ModifiedBy?: any;
  UserIpAddress?: any;
  CreateDate: string;
  LastModified: string;
  ActiveStatus: boolean;
  IsApprovalRequired?: any;
  RouteId?: any;
  ApprovalTypeId?: any;
  IsDeleted: boolean;
}

const UpdateZone: React.FC<ComponentProps> = ({ handleClose, zoneName, zoneId, state }) => {
    const { showNotification } = useContext(NotificationContext);
    const queryClient = useQueryClient();

    const getStates = async (): Promise<State[]> => {
        return (await apiCall().get('/State/GetAllStates')).data;
    };

    const { data, isLoading, isError, error } = useQuery(['get-states'], getStates, {
        retry: 0,
        keepPreviousData: true,
        enabled: true
    });

    const getStateMessage = error as AxiosError;
    const getStateErrorMessage = getStateMessage?.message;

    const updateZone = (data: Zone) => {
        return apiCall().put(`/Zone/updateZone/${zoneId}`, data);
    };

    const updateZoneMutation = useMutation(['update-zone'], updateZone, {retry: 0});

    const updateZoneHandler = async (values: FormikValues) => {
        const payload: Zone = {
            ZoneName: values?.name,
        };

        try {
            await updateZoneMutation.mutateAsync(payload, {
                onSuccess: () => {
                    showNotification({
                        message: 'Zone successfully updated.',
                        type: 'success',
                    });

                    queryClient.refetchQueries(['get-zones']);
                    handleClose();
                }
            })
        } catch (error: any){
            showNotification({
                message: error?.response?.data?.title || error?.message,
                type: 'error',
            });
        }
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().matches(/^[A-Za-z\s!@#$%^&*()/]+$/, 'Only text is allowed').required('required'),
    });

    const formik = useFormik<FormikValues>({
        initialValues: {
            name: zoneName ? zoneName : '',
            state: state || ''
        },
        onSubmit: (values) => {
            updateZoneHandler(values);
        },
        validationSchema,
    });

    const stateOptions = data && data?.length > 0 ? (
        data?.map((state: State) => (
            <option key={state?.StateId} value={state?.StateId}>
                {state?.StateName}
            </option>
        ))
    ) : (
        <option disabled>
            {isLoading ? "Loading..." : isError ? getStateErrorMessage : ''}
        </option>
    )

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <Field 
                        name='state'
                        type='text'
                        as={Select}
                        label='State'
                        options={stateOptions}
                        placeholder="Choose State"
                    />
                    <Field 
                        name='name'
                        type='text'
                        as={Input}
                        label='Zone Name'
                        placeholder='Name'
                    />
                </FormWrapper>
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton onClick={handleClose} text='Cancel'/>
                        <StyledButton 
                            type="submit" 
                            text={updateZoneMutation.isLoading ? 'Updating...' : 'Update'}
                            disabled={updateZoneMutation.isLoading}
                        />
                    </ButtonContainer>
                </ButtonWrapper>
            </form>
        </FormikProvider>
    )
};

export default UpdateZone;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

